/**
 * Generic "dismiss by dragging down" wrapper for floating surfaces (popovers,
 * light sheets) that are not Kobalte's `Dialog`.
 *
 * Stacks the shared physics (`usePullToDismiss` on top of the `@/lib/gestures`
 * recognizer) with a minimal presentation: an optional handle bar that reveals
 * "drag to close" past the threshold and the exit transform. It respects
 * `prefers-reduced-motion` (direct close, no animation) via the hook.
 *
 * `Dialog` does not use this wrapper because it has its own Portal/Overlay and
 * its already-built indicator; it shares the SAME physics by calling the hook
 * directly.
 */
import { msg } from "@lingui/core/macro";
import { ArrowDown } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@apunte-digital/shared";

import { usePullToDismiss } from "@/lib/gestures/use-pull-to-dismiss";
import { useI18n } from "@/providers/i18n-provider";

interface PullToDismissProps extends JSX.HTMLAttributes<HTMLDivElement> {
  /** Closes the surface when the drag confirms. */
  onDismiss: () => void;
  /** Disables the gesture without unmounting. */
  disabled?: boolean;
  /** Hides the handle bar (some surfaces prefer their own handle). */
  hideHandle?: boolean;
  children?: JSX.Element;
}

export function PullToDismiss(props: PullToDismissProps) {
  const [local, others] = splitProps(props, [
    "onDismiss",
    "disabled",
    "hideHandle",
    "children",
    "class",
    "style",
  ]);
  const { t } = useI18n();

  let surface: HTMLDivElement | undefined;
  const gesture = usePullToDismiss({
    onDismiss: () => local.onDismiss(),
    disabled: () => local.disabled === true,
    getSurfaceRef: () => surface,
  });

  const pastThreshold = () => gesture.translateY() >= gesture.commitThresholdPx;

  return (
    <div
      ref={(element) => {
        surface = element;
      }}
      class={cn("select-none", local.class)}
      style={{
        transform:
          gesture.translateY() === 0 ? undefined : `translate3d(0, ${gesture.translateY()}px, 0)`,
        transition: gesture.isAnimating()
          ? `transform ${gesture.transitionMs}ms cubic-bezier(0.32, 0.72, 0, 1)`
          : undefined,
        "will-change": gesture.translateY() === 0 ? "auto" : "transform",
        ...(typeof local.style === "object" ? local.style : {}),
      }}
      onPointerDown={gesture.handlePointerDown}
      onPointerMove={gesture.handlePointerMove}
      onPointerUp={gesture.handlePointerUp}
      onPointerCancel={gesture.handlePointerCancel}
      {...others}
    >
      <Show when={!local.hideHandle}>
        <div class="drag-handle-bar flex flex-col items-center justify-center pt-1 pb-2 cursor-row-resize">
          <div
            aria-hidden="true"
            class="flex items-center gap-1.5 overflow-hidden text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-all duration-300"
            style={{
              height: gesture.translateY() > gesture.indicatorTriggerPx ? "16px" : "0px",
              opacity: gesture.translateY() > gesture.indicatorTriggerPx ? 1 : 0,
            }}
          >
            <ArrowDown
              class={cn(
                "h-3 w-3 transition-transform duration-300",
                pastThreshold() && "rotate-180 scale-125 text-destructive",
              )}
            />
            <span>{pastThreshold() ? t(msg`Release to close`) : t(msg`Pull down to close`)}</span>
          </div>
          <div
            class={cn(
              "h-1.5 w-12 rounded-full bg-muted-foreground/35 transition-all duration-300",
              pastThreshold() && "w-16 bg-destructive",
            )}
          />
        </div>
      </Show>
      {local.children}
    </div>
  );
}
